import { DataFileInterface } from '@systemeio/file-manager'
import { toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { DomainId } from 'modules/profile/mailing-settings/types/mailing-settings-domain-interface'
import { useEffect, useState } from 'react'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useModal } from 'shared/hooks/use-modal'
import { useOnceTrue } from 'shared/hooks/use-once-true'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { useCourse } from '../../../hooks/use-course'
import { CourseEditApiInterface, CourseId } from '../../../types/course-interface'
import { useLightCourseActions } from '../../hooks/use-light-course-actions'
import { PatchErrorsLightCourseOptionsInterface } from '../../light-course-interface'

export const useSaveOnSubmit = ({ courseId }: { courseId?: CourseId }) => {
  const { t } = useLocoTranslation()
  const [isFetching, setIsFetching] = useState(false)
  const [errors, setErrors] = useState<PatchErrorsLightCourseOptionsInterface>()
  const [tempState, setTempState] = useState<Partial<CourseEditApiInterface>>()
  const { courseData, mutate } = useCourse(courseId)
  const { patchLightCourseOptions } = useLightCourseActions()
  const domainConfirmationModal = useModal<DomainId>()
  const isModalWasOpenOnce = useOnceTrue(domainConfirmationModal.opened)
  const { revalidateModules } = useRevalidateModules()
  const [isCodeValid, setIsCodeValid] = useState(true)

  useEffect(() => {
    if (courseData) {
      setTempState({ ...courseData })
    } else {
      setTempState(undefined)
    }
  }, [courseData?.id])

  const handleFormSubmit = async () => {
    if (!tempState) return
    if (!courseId) return
    if (isFetching) return
    if (!isCodeValid) return

    setIsFetching(true)
    setErrors(undefined)

    try {
      const updatedCourse = await patchLightCourseOptions(courseId, {
        name: tempState.name,
        description: tempState.description,
        embeddedCode: tempState.embeddedCode || null,
        logo: tempState.logo ? tempState.logo.id : null,
        image: tempState.image ? tempState.image.id : null,
        path: tempState.path,
        domainId: tempState.domainId,
        instructorName: tempState.instructorName,
        teacherPhoto: tempState.teacherPhoto ? tempState.teacherPhoto.id : null,
        issueCertificateUponCompletion: tempState.issueCertificateUponCompletion,
        certificateLogo: tempState.certificateLogo ? tempState.certificateLogo.id : null,
        signature: tempState.signature ? tempState.signature.id : null,
      })

      await mutate(updatedCourse, false)
      await revalidateModules([RequestModuleEnum.MembershipLightCourse])

      toast.success(t('global.changes_saved'))
    } catch (error) {
      toast.error(t('global.error'))
      if (error instanceof BadRequest) {
        setErrors(error.errors.fields)
        return
      }
    } finally {
      setIsFetching(false)
    }
  }

  const handleTeacherPhotoChange = async (file?: DataFileInterface) => {
    setTempState(prev => {
      if (!prev) return prev
      return {
        ...prev,
        teacherPhoto: file ?? null,
      }
    })
  }

  const handleCourseLogoChange = async (file?: DataFileInterface) => {
    setTempState(prev => {
      if (!prev) return prev
      return {
        ...prev,
        logo: file ?? null,
      }
    })
  }

  const handleCourseCoverChange = async (file?: DataFileInterface) => {
    setTempState(prev => {
      if (!prev) return prev
      return {
        ...prev,
        image: file ?? null,
      }
    })
  }

  const handleCertificateLogoChange = async (file?: DataFileInterface) => {
    setTempState(prev => {
      if (!prev) return prev
      return {
        ...prev,
        certificateLogo: file ?? null,
      }
    })
  }

  const handleSignatureChange = async (file?: DataFileInterface) => {
    setTempState(prev => {
      if (!prev) return prev
      return {
        ...prev,
        signature: file ?? null,
      }
    })
  }

  const handleDomainChange = async (domainId?: DomainId) => {
    if (isModalWasOpenOnce) {
      setTempState(prev => {
        if (!prev) return prev
        return {
          ...prev,
          ...(domainId ? { domainId: domainId } : {}),
        }
      })
    } else {
      domainConfirmationModal.open(domainId)
    }
  }

  const handleDomainChangeConfirm = async () => {
    setTempState(prev => {
      if (!prev) return prev
      return {
        ...prev,
        ...(domainConfirmationModal.data ? { domainId: domainConfirmationModal.data } : {}),
      }
    })
  }

  const handleIssueCertificateUponCompletionChange = async (value: boolean) => {
    setTempState(prev => {
      if (!prev) return prev
      return {
        ...prev,
        issueCertificateUponCompletion: value ?? null,
      }
    })
  }

  return {
    tempState,
    setTempState,
    courseData,
    errors,
    isFetching,

    handleTeacherPhotoChange,
    handleCourseLogoChange,
    handleCourseCoverChange,
    handleCertificateLogoChange,
    handleSignatureChange,
    handleIssueCertificateUponCompletionChange,

    domainConfirmationModal,
    handleDomainChange,
    handleDomainChangeConfirm,

    handleFormSubmit,

    isCodeValid,
    setIsCodeValid,
  }
}
